// @flow
// vendor libs
import BackboneEventsShim from 'app/common/lib/BackboneEventsShim';

// Module dependencies
import LruCache from 'app/common/lib/LruCache';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import user from 'app/common/lib/User';

const NUM_ITEMS_TO_REQUEST = 5;

/**
 * @constructor
 * @param {Object} options
 */
const TypeaheadProvider = function(options: Object) {
  options = options || {};
  this.tags = options.tags;
  this.enable_recent_queries = !!options.enable_recent_queries;
  this.minimum_term_character_length = options.minimum_term_character_length || 0;
  this.bypass_lru_cache = options.bypass_lru_cache;

  const searchResourceOptions: Object = {
    tags: options.tags,
    pin: options.pin_id,
  };

  const numPlaces = options.num_places;
  if (numPlaces) {
    searchResourceOptions.num_places = numPlaces;
  } else {
    // 'count' is used in the resource to set the number of items to return.
    // TODO (connor) — remove this and solely use num_* to define the max
    //  number of items to return.
    searchResourceOptions.count = 'count' in options ? options.count : NUM_ITEMS_TO_REQUEST;
  }

  if (options.exclude_non_followers) {
    searchResourceOptions.exclude_non_followers = true;
  }

  if (options.field_set_key) {
    searchResourceOptions.field_set_key = options.field_set_key;
  }

  if (options.pin_scope) {
    searchResourceOptions.pin_scope = options.pin_scope;
  }

  let resourceName = 'TypeaheadResource';
  if (options.resource_name) {
    resourceName = options.resource_name;
  }

  this.searchResource = ResourceFactory.create(resourceName, searchResourceOptions);

  this.itemsCache = new LruCache();
};

Object.assign(
  TypeaheadProvider.prototype,
  /** @lends {TypeaheadProvider.prototype} */ BackboneEventsShim,
);

Object.assign(
  TypeaheadProvider.prototype,
  /** @lends {TypeaheadProvider.prototype} */ {
    /**
     * Search for typeahead results for a given term.
     * @param {string} term The search term
     * @param {?Function} callback The function to call when items are ready
     */
    search(term, callback) {
      let tags;

      if (!term && this.tags === 'pinners_and_contacts') {
        this.abortSearch();
        return this.publishData(term, [], {}, callback);
      }

      if (!term && user.isLimitedLogin()) {
        this.abortSearch();
        return this.publishData(term, [], {}, callback);
      }

      if (!term && this.enable_recent_queries && user && user.isAuthenticated()) {
        tags = 'recent_search';
      } else {
        const data = this.bypass_lru_cache ? {} : this.itemsCache.get(term);
        if (data) {
          const items = data.items;
          if (items && items.length) {
            this.abortSearch();
            const hint = data.hint;
            this.publishData(term, items, hint, callback);
            return undefined;
          }
        }
      }

      if (term.length >= this.minimum_term_character_length) {
        this.fetch(term, tags, callback);
      } else {
        this.publishData(term, [], {}, callback);
      }
      return undefined;
    },

    /**
     * Get the typeahead's resource.
     *
     * @return {!IResource} The typeahead's resource.
     */
    getResource() {
      return this.searchResource;
    },

    /*
   * Abort current search
   */
    abortSearch() {
      this.getResource().abort();
    },

    /**
     * Trigger PCONST.EVENT_DATA event and call optional callback
     * @param {string} term The search term
     * @param {Array.<Object>} items Typeahead results for term
     * @param {Object} hint Typeahead hint for term
     * @param {?Function} callback The function to call with data
     * @private
     */
    publishData(term, items, hint, callback) {
      const data = {
        term,
        items,
        hint,
      };
      this.trigger(PCONST.EVENT_DATA, data);
      if (callback) {
        callback(data);
      }
    },

    /**
     * Set the autocomplete scope for create autocomplete links.
     * @param {string} autocompleteScope The scope for query links in autocomplete result.
     */
    setAutocompleteScope(autocompleteScope) {
      this.getResource().set('autocompleteScope', autocompleteScope);
    },

    /**
     * Fetch typeahead results from server
     * @param {string} term The search term
     * @param {?string} tags Optional tags.
     * @param {?Function} callback The function to call when items are ready
     * @private
     */
    fetch(term, tags, callback) {
      /** @type {!IResource} */
      const resource = this.getResource();

      if (resource.isCalling()) {
        const oldTerm = resource.get('term');
        if (term === oldTerm) {
          return;
        } else {
          this.abortSearch();
        }
      }

      // Use an empty string for this.tags in the case where the resource
      // data isn't wired up yet.
      if (this.tags === '') {
        return;
      }

      resource.set('term', term);
      resource.set('tags', tags || this.tags);
      resource.resetBookmark();
      resource.callGet({ showError: false }).then(
        (resourceResponse) => {
          const data = resourceResponse.resource_response.data;
          const items = data.items;
          const hint = data.hint || {};
          const dataToCache = {
            hint,
            items,
          };
          this.cacheData(term, dataToCache);
          this.publishData(term, items, hint, callback);
        },
        (error) => {
          this.trigger('fetchError', error);
        },
      );
      this.warm = true;
    },

    /**
     * Remove a cached item from itemsCache.
     * @param  {string} term The term to remove cache for.
     */
    removeCacheForTerm(term) {
      this.itemsCache.remove(term);
    },

    /**
     * Cache items for term
     * @param {string} term The search term
     * @param {Array.<Object>} items Typeahead results and hints for term
     * @private
     */
    cacheData(term, items) {
      this.itemsCache.set(term, items);
    },

    /*
   * Warm up typeahead
   */
    prefetch() {
      if (this.warm) {
        return;
      }

      const resource = ResourceFactory.create('TypeaheadPrepareResource');
      resource.callGet({ showError: false });
      this.warm = true;
    },
  },
);

export default TypeaheadProvider;
